import { API_URL, formatApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import SearchableCourseSelect from '../components/SearchableCourseSelect';
import SearchableCashierSelect from '../components/SearchableCashierSelect';
import {
  CreditCard, ChatText, PencilSimple, Download, Spinner, FloppyDisk,
  ArrowLeft, Drop, Lightning, TrendUp, X, Wallet,
  GraduationCap, IdentificationBadge, Phone, Buildings, Eye, EyeSlash,
  PaperPlaneTilt, CheckCircle, WarningCircle,
  UserCircle, Receipt, ArrowsClockwise,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  AreaChart, Area, BarChart, Bar, XAxis, ResponsiveContainer,
} from 'recharts';
import { SkeletonHero } from '../components/ui/page-skeleton';

const TABS = [
  { key: 'personal',     label: 'Personal',     icon: UserCircle },
  { key: 'service',      label: 'Service',      icon: GraduationCap },
  { key: 'transactions', label: 'Transactions', icon: Receipt },
  { key: 'messaging',    label: 'Messages',     icon: ChatText },
  { key: 'renewal',      label: 'Renewals',     icon: ArrowsClockwise },
];

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Build last-6-months bucket aggregated from real transactions
const buildMonthlyBuckets = (txns) => {
  const buckets = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    buckets.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, m: MONTH_NAMES[d.getMonth()], r: 0, v: 0 });
  }
  const idx = Object.fromEntries(buckets.map((b, i) => [b.key, i]));
  (txns || []).forEach(t => {
    const dStr = (t.created_at || '').substring(0, 7); // YYYY-MM
    if (idx[dStr] !== undefined) {
      const i = idx[dStr];
      buckets[i].r += Number(t.amount || 0);
      buckets[i].v += 1;
    }
  });
  return buckets;
};

export default function SubscriberDetailPage() {
  const { id } = useParams();
  const { isSuperAdmin } = useAuth();
  const { selectedEntity, entities } = useTenant();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('personal');
  const [idTypes, setIdTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txPage, setTxPage] = useState(1);
  const [txPageSize, setTxPageSize] = useState(10);
  const [stmtFrom, setStmtFrom] = useState('');
  const [stmtTo, setStmtTo] = useState('');
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsBody, setSmsBody] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [renewalForm, setRenewalForm] = useState({ new_card: '', plan: 'Fee', reason: '', description: '', agent_id: '' });
  const [renewalSubmitting, setRenewalSubmitting] = useState(false);
  const [cashiers, setCashiers] = useState([]);
  const [cashiersLoading, setCashiersLoading] = useState(false);
  const [courses, setCourses] = useState([]);

  const hdrs = () => { const t = localStorage.getItem('access_token'); return t ? { Authorization: `Bearer ${t}` } : {}; };

  const fetchSubscriber = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/subscribers/${id}`, { withCredentials: true, headers: hdrs() });
      setSub(data);
      setForm({
        sub_name: data.sub_name || '',
        mobile: data.mobile || '',
        mobile_2: data.mobile_2 || '',
        email: data.email || '',
        gender: data.gender || 'M',
        category: data.category || '',
        student_id: data.student_id || '',
        student_course_id: data.student_course_id || '',
        curr_year_of_study: data.curr_year_of_study || '1',
        id_type: data.id_type || '1',
        id_number: data.id_number || '',
        relationship_agent: data.relationship_agent || '',
        status: data.status || 'active',
        campus_id: data.campus_id || '',
      });
    } catch (e) { toast.error('Failed to load subscriber'); navigate('/subscribers'); } finally { setLoading(false); }
  }, [id, navigate]);

  const fetchIdTypes = useCallback(async () => { try { const { data } = await axios.get(`${API_URL}/api/subscriber-id-types`, { withCredentials: true, headers: hdrs() }); setIdTypes(data); } catch (e) {} }, []);
  const fetchCategories = useCallback(async () => { try { const { data } = await axios.get(`${API_URL}/api/subscriber-categories`, { withCredentials: true, headers: hdrs() }); setCategories(data); } catch (e) {} }, []);
  const fetchCampuses = useCallback(async () => { try { const { data } = await axios.get(`${API_URL}/api/campuses`, { withCredentials: true, headers: hdrs() }); setCampuses(data); } catch (e) {} }, []);
  const fetchCashiers = useCallback(async () => {
    setCashiersLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/cashiers`, { withCredentials: true, headers: hdrs() });
      setCashiers(Array.isArray(data) ? data : []);
    } catch (e) { setCashiers([]); }
    finally { setCashiersLoading(false); }
  }, []);
  const fetchCourses = useCallback(async (campusId) => {
    if (!campusId) { setCourses([]); return; }
    try {
      const { data } = await axios.get(`${API_URL}/api/student-courses?campus_id=${campusId}`, { withCredentials: true, headers: hdrs() });
      setCourses(Array.isArray(data) ? data : []);
    } catch (e) { setCourses([]); }
  }, []);
  const fetchTransactions = useCallback(async () => { setTxLoading(true); try { const { data } = await axios.get(`${API_URL}/api/subscribers/${id}/transactions`, { withCredentials: true, headers: hdrs() }); setTransactions(Array.isArray(data) ? data : []); setTxPage(1); } catch (e) { setTransactions([]); } finally { setTxLoading(false); } }, [id]);
  const fetchMessages = useCallback(async () => { setMsgLoading(true); try { const { data } = await axios.get(`${API_URL}/api/subscribers/${id}/messages`, { withCredentials: true, headers: hdrs() }); setMessages(Array.isArray(data) ? data : []); } catch (e) { setMessages([]); } finally { setMsgLoading(false); } }, [id]);

  const openSmsModal = () => { setSmsBody(''); setSmsOpen(true); };
  const submitSms = async () => {
    const msg = (smsBody || '').trim();
    if (!msg) { toast.error('Message is required'); return; }
    if (!sub?.mobile) { toast.error('Subscriber has no mobile on file'); return; }
    setSmsSending(true);
    try {
      await axios.post(`${API_URL}/api/subscribers/${id}/send-sms`, { message: msg }, { withCredentials: true, headers: hdrs() });
      toast.success('SMS sent successfully');
      setSmsOpen(false);
      setSmsBody('');
      // Refresh messages list (in case tab is open) and switch user there for confirmation
      fetchMessages();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to send SMS'));
    } finally {
      setSmsSending(false);
    }
  };

  useEffect(() => { fetchSubscriber(); fetchIdTypes(); fetchCategories(); fetchCampuses(); fetchTransactions(); fetchCashiers(); }, [fetchSubscriber, fetchIdTypes, fetchCategories, fetchCampuses, fetchTransactions, fetchCashiers]);
  useEffect(() => { if (sub?.campus_id) fetchCourses(sub.campus_id); }, [sub?.campus_id, fetchCourses]);
  useEffect(() => { if (activeTab === 'messaging') fetchMessages(); }, [activeTab, fetchMessages]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        sub_name: form.sub_name,
        mobile: form.mobile,
        mobile_2: form.mobile_2 || '',
        email: form.email || '',
        gender: form.gender || 'N/A',
        campus_id: form.campus_id,
        category: form.category,
        student_id: form.student_id || '',
        student_course_id: form.student_course_id || '',
        curr_year_of_study: form.curr_year_of_study || '',
        id_type: form.id_type || '1',
        id_number: form.id_number || '',
        relationship_agent: form.relationship_agent || '',
      };
      await axios.put(`${API_URL}/api/subscribers/${id}`, payload, { withCredentials: true, headers: hdrs() });
      toast.success('Subscriber updated');
      setIsEditing(false);
      fetchSubscriber();
    } catch (e) { toast.error(formatApiError(e, 'Failed to update subscriber')); } finally { setSaving(false); }
  };
  const cancelEdit = () => { setIsEditing(false); fetchSubscriber(); };
  const generateStatement = async () => {
    if (!stmtFrom || !stmtTo) { toast.error('Select a date range'); return; }
    try {
      toast.info('Generating statement…');
      const token = localStorage.getItem('access_token');
      const res = await fetch(
        `${API_URL}/api/subscribers/${id}/statement?date_from=${stmtFrom}&date_to=${stmtTo}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(errText || `Failed (${res.status})`);
      }
      const blob = await res.blob();
      let filename = `Statement_${sub?.card_no || id}_${stmtFrom}_${stmtTo}.pdf`;
      const cd = res.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      if (match) filename = match[1];
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success('Statement downloaded');
    } catch (e) {
      toast.error(e.message || 'Failed to generate statement');
    }
  };

  if (loading) return <SkeletonHero testid="subscriber-detail-loading" />;
  if (!sub) return null;
  const name = sub.sub_name || '';
  // Wallet balance comes from the upstream's `wallet_bal` (mapped to
  // `card_balance` on the local detail payload). Prefer the explicit
  // `wallet_bal` if present (e.g. from a future refactor) so this stays
  // correct without further changes.
  const bal = Number(sub.wallet_bal ?? sub.card_balance ?? 0);

  // ===== Real-data derived metrics =====
  // A "recharge" event for KPIs/sparkline is any positive-credit txn — this
  // INCLUDES the one-off "Initial Recharge" emitted when a subscriber is
  // first registered, since from the wallet's perspective that's still
  // money loaded onto the card. Card-recharge top-ups + initial recharges
  // therefore both contribute to total/avg/this-month figures and the
  // 6-month trend sparkline.
  const recharges = (transactions || []).filter(t => Number(t.amount || 0) > 0);
  const totalRecharges = recharges.reduce((s, t) => s + Number(t.amount || 0), 0);
  const rechargeCount = recharges.length;
  const avgRecharge = rechargeCount ? totalRecharges / rechargeCount : 0;
  const lastRecharge = recharges[0]; // already sorted DESC by created_at on the API
  // Currency: derive from the SUBSCRIBER'S own entity (single source of
  // truth). For an entity admin this matches `selectedEntity`; for a super
  // admin viewing a subscriber outside their current scope we look it up
  // in the `entities` list loaded by TenantContext. We deliberately do NOT
  // trust `txn.currency` here because we've observed the upstream tagging
  // wallet rows with the system-default currency rather than the entity's
  // actual currency (Uganda txns tagged "TZS" in production).
  const subEntityId = sub.entity_id || sub.middleware_id_entity || '';
  const subEntity =
    (selectedEntity && String(selectedEntity.id) === String(subEntityId) ? selectedEntity : null)
    || (entities || []).find(e => String(e.id) === String(subEntityId));
  const currency = subEntity?.currency_code || selectedEntity?.currency_code || '';
  const daysSinceReg = sub.created_at
    ? Math.max(1, Math.floor((Date.now() - new Date(sub.created_at).getTime()) / 86400000))
    : 0;
  const monthlyBuckets = buildMonthlyBuckets(recharges);
  const thisMonthAmt = monthlyBuckets[monthlyBuckets.length - 1]?.r || 0;
  const lastMonthAmt = monthlyBuckets[monthlyBuckets.length - 2]?.r || 0;
  const momChange = lastMonthAmt > 0
    ? ((thisMonthAmt - lastMonthAmt) / lastMonthAmt) * 100
    : (thisMonthAmt > 0 ? 100 : 0);
  const momLabel = lastMonthAmt > 0
    ? `${momChange >= 0 ? '+' : ''}${momChange.toFixed(0)}% vs last month`
    : (thisMonthAmt > 0 ? 'First active month' : 'No recharges yet');

  return (
    <div data-testid="subscriber-detail-page" className="space-y-4 pb-8">
      {/* Back link */}
      <button onClick={() => navigate('/subscribers')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors" data-testid="back-to-subscribers">
        <ArrowLeft size={16} /> Back to Subscribers
      </button>

      {/* ===== HERO PROFILE PANEL ===== */}
      <SubscriberHero
        sub={sub}
        name={name}
        bal={bal}
        currency={currency}
        totalRecharges={totalRecharges}
        rechargeCount={rechargeCount}
        avgRecharge={avgRecharge}
        lastRecharge={lastRecharge}
        daysSinceReg={daysSinceReg}
        monthlyBuckets={monthlyBuckets}
        momChange={momChange}
        momLabel={momLabel}
        thisMonthAmt={thisMonthAmt}
        lastMonthAmt={lastMonthAmt}
        isEditing={isEditing}
        saving={saving}
        onSave={handleSave}
        onEdit={() => setIsEditing(true)}
        onCancel={cancelEdit}
        stmtFrom={stmtFrom}
        stmtTo={stmtTo}
        onStmtFromChange={setStmtFrom}
        onStmtToChange={setStmtTo}
        onGenerateStmt={generateStatement}
        isSuperAdmin={isSuperAdmin}
        onSendSms={openSmsModal}
      />

      {/* ===== ROW 2: TABS (top, horizontal) ===== */}
      <div className="bg-[#FFFFFF] border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex items-center gap-1 px-2 py-2 border-b border-slate-200 overflow-x-auto bg-slate-50/50" style={{ scrollbarWidth: 'none' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`tab-${tab.key}`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg whitespace-nowrap transition-all ${
                  activeTab === tab.key
                    ? 'bg-[#FFFFFF] text-slate-900 shadow-sm border border-slate-200'
                    : 'text-slate-500 hover:text-slate-900 hover:bg-[#FFFFFF]/60'
                }`}>
                {Icon && <Icon size={15} weight={activeTab === tab.key ? 'fill' : 'regular'} />}
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ===== TAB CONTENT ===== */}
        <div className="p-6 min-h-[340px]">
          {activeTab === 'personal' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-5 max-w-4xl" data-testid="tab-personal-content">
              <FField label="Mobile" required><Input value={form.mobile} onChange={e => setForm(p => ({ ...p, mobile: e.target.value }))} readOnly={!isEditing} className={!isEditing ? 'bg-slate-50' : ''} data-testid="field-mobile" /></FField>
              <FField label="Alt. Mobile"><Input value={form.mobile_2} onChange={e => setForm(p => ({ ...p, mobile_2: e.target.value }))} readOnly={!isEditing} className={!isEditing ? 'bg-slate-50' : ''} data-testid="field-alt-mobile" /></FField>
              <FField label="Registered"><Input value={sub.created_at ? sub.created_at.substring(0, 10) : ''} readOnly className="bg-slate-50" /></FField>
              <FField label="Full Name" required><Input value={form.sub_name} onChange={e => setForm(p => ({ ...p, sub_name: e.target.value }))} readOnly={!isEditing} className={!isEditing ? 'bg-slate-50' : ''} data-testid="field-name" /></FField>
              <FField label="Gender" required>
                <div className="flex gap-6 pt-2">
                  {['M', 'F'].map(g => (
                    <label key={g} className={`flex items-center gap-2 text-sm ${isEditing ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}>
                      <input type="radio" name="gender" disabled={!isEditing} checked={form.gender === g} onChange={() => setForm(p => ({ ...p, gender: g }))} className="accent-slate-900" data-testid={`field-gender-${g.toLowerCase()}`} />
                      {g === 'M' ? 'Male' : 'Female'}
                    </label>
                  ))}
                </div>
              </FField>
              <FField label="Category" required>
                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))} disabled={!isEditing}>
                  <SelectTrigger data-testid="field-category" className={!isEditing ? 'bg-slate-50' : ''}><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.category} value={c.category}>{c.category}</SelectItem>)}</SelectContent>
                </Select>
              </FField>
              <FField label="Email"><Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} readOnly={!isEditing} className={!isEditing ? 'bg-slate-50' : ''} data-testid="field-email" /></FField>
              <FField label="ID Type">
                <Select value={form.id_type} onValueChange={v => setForm(p => ({ ...p, id_type: v }))} disabled={!isEditing}>
                  <SelectTrigger data-testid="field-id-type" className={!isEditing ? 'bg-slate-50' : ''}><SelectValue /></SelectTrigger>
                  <SelectContent>{idTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.id_name}</SelectItem>)}</SelectContent>
                </Select>
              </FField>
              <FField label="ID Number"><Input value={form.id_number} onChange={e => setForm(p => ({ ...p, id_number: e.target.value }))} readOnly={!isEditing} className={!isEditing ? 'bg-slate-50' : ''} data-testid="field-id-number" /></FField>

              {(form.category || '').toUpperCase() === 'STUDENT' && (
                <>
                  <FField label="Student ID"><Input value={form.student_id} onChange={e => setForm(p => ({ ...p, student_id: e.target.value }))} readOnly={!isEditing} className={!isEditing ? 'bg-slate-50' : ''} data-testid="field-student-id" /></FField>
                  <FField label="Course Name">
                    {isEditing ? (
                      <SearchableCourseSelect
                        value={form.student_course_id}
                        onChange={(v) => setForm(p => ({ ...p, student_course_id: v }))}
                        courses={courses}
                        placeholder="Select course"
                        testid="field-course-name"
                      />
                    ) : (
                      <Input
                        value={(courses.find(c => String(c.id) === String(form.student_course_id))?.course_name) || form.student_course_id || ''}
                        readOnly
                        className="bg-slate-50"
                        data-testid="field-course-name"
                      />
                    )}
                  </FField>
                  <FField label="Year of Study">
                    <Select value={form.curr_year_of_study} onValueChange={v => setForm(p => ({ ...p, curr_year_of_study: v }))} disabled={!isEditing}>
                      <SelectTrigger data-testid="field-year" className={!isEditing ? 'bg-slate-50' : ''}><SelectValue /></SelectTrigger>
                      <SelectContent>{['1','2','3','4','5','6','7'].map(y => <SelectItem key={y} value={y}>Year {y}</SelectItem>)}</SelectContent>
                    </Select>
                  </FField>
                </>
              )}

              <FField label="Relationship Agent">
                {isEditing ? (
                  <Select
                    value={form.relationship_agent}
                    onValueChange={v => setForm(p => ({ ...p, relationship_agent: v }))}
                  >
                    <SelectTrigger data-testid="field-rel-agent"><SelectValue placeholder="Select cashier" /></SelectTrigger>
                    <SelectContent>
                      {cashiers
                        .filter(c => c.user_mobile && String(c.user_mobile).replace(/\D/g, '').length >= 9)
                        .map(c => {
                          const mob = String(c.user_mobile).replace(/\D/g, '');
                          return <SelectItem key={c.id} value={mob}>{c.user_fullname || c.user_name || mob}</SelectItem>;
                        })}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={(() => {
                      const ra = String(form.relationship_agent || '').replace(/\D/g, '');
                      if (!ra) return '';
                      const match = cashiers.find(c => String(c.user_mobile || '').replace(/\D/g, '') === ra);
                      return match ? (match.user_fullname || match.user_name || ra) : (form.relationship_agent || '');
                    })()}
                    readOnly
                    className="bg-slate-50"
                    data-testid="field-rel-agent"
                  />
                )}
              </FField>
            </div>
          )}

          {activeTab === 'service' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-5 max-w-4xl" data-testid="tab-service-content">
              <FField label="Card No"><Input value={sub.card_no || ''} readOnly className="bg-slate-50" /></FField>
              <FField label="Status">
                <Input value={(form.status || 'active').toUpperCase()} readOnly className="bg-slate-50" data-testid="field-status" />
              </FField>
              <FField label="Campus" required>
                <Select value={form.campus_id} onValueChange={v => setForm(p => ({ ...p, campus_id: v }))} disabled={!isEditing}>
                  <SelectTrigger data-testid="field-campus" className={!isEditing ? 'bg-slate-50' : ''}><SelectValue /></SelectTrigger>
                  <SelectContent>{campuses.map(c => <SelectItem key={c.id} value={c.id}>{c.campus_name}</SelectItem>)}</SelectContent>
                </Select>
              </FField>
              <FField label="Wallet Balance"><Input value={bal.toLocaleString()} readOnly className="bg-slate-50" /></FField>
              <FField label="Subscription"><Input value={sub.subscription_plan || '-'} readOnly className="bg-slate-50" /></FField>
              {isSuperAdmin && (
                <FField label="Entity"><Input value={sub.entity_name || '-'} readOnly className="bg-slate-50" /></FField>
              )}
            </div>
          )}

          {activeTab === 'transactions' && (
            <div data-testid="tab-transactions-content">
              {txLoading ? <div className="py-2"><div className="space-y-2">{Array.from({length:6}).map((_,i)=>(<div key={i} className="skeleton h-14 rounded" style={{width:`${88 - (i%3)*5}%`}}/>))}</div></div>
              : transactions.length === 0 ? <p className="text-slate-400 py-12 text-center text-sm">No transactions recorded yet</p>
              : (() => {
                const totalPages = Math.max(1, Math.ceil(transactions.length / txPageSize));
                const currentPage = Math.min(txPage, totalPages);
                const startIdx = (currentPage - 1) * txPageSize;
                const endIdx = Math.min(startIdx + txPageSize, transactions.length);
                const pageRows = transactions.slice(startIdx, endIdx);
                return (
                <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left py-3 px-3 text-[10px] tracking-[0.12em] uppercase text-slate-500 font-semibold">Ref No.</th>
                        <th className="text-left py-3 px-3 text-[10px] tracking-[0.12em] uppercase text-slate-500 font-semibold">Card</th>
                        <th className="text-right py-3 px-3 text-[10px] tracking-[0.12em] uppercase text-slate-500 font-semibold">Amount</th>
                        <th className="text-left py-3 px-3 text-[10px] tracking-[0.12em] uppercase text-slate-500 font-semibold">Date</th>
                        <th className="text-left py-3 px-3 text-[10px] tracking-[0.12em] uppercase text-slate-500 font-semibold">Channel</th>
                        <th className="text-left py-3 px-3 text-[10px] tracking-[0.12em] uppercase text-slate-500 font-semibold">Store</th>
                        <th className="text-left py-3 px-3 text-[10px] tracking-[0.12em] uppercase text-slate-500 font-semibold">Narration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.map((t, i) => {
                        const amt = Number(t.amount || 0);
                        const isCredit = amt >= 0;
                        return (
                          <tr key={t.id || `${startIdx}-${i}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                            <td className="py-3 px-3 font-mono text-slate-700">{t.trn_ref_no || '-'}</td>
                            <td className="py-3 px-3 font-mono">{t.card_no || sub.card_no}</td>
                            <td className={`py-3 px-3 text-right font-bold font-mono ${isCredit ? 'text-emerald-700' : 'text-rose-700'}`}>
                              {isCredit ? '+' : ''}{amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="py-3 px-3 text-slate-500 font-mono">{(t.created_at || '').substring(0, 16).replace('T', ' ')}</td>
                            <td className="py-3 px-3 text-slate-500">{t.channel || '-'}</td>
                            <td className="py-3 px-3 text-slate-700">{t.store_name || '—'}</td>
                            <td className="py-3 px-3">
                              <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full ${
                                isCredit ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                              }`}>
                                {t.narration || (isCredit ? 'CREDIT' : 'DEBIT')}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-200" data-testid="tx-pagination">
                  <div className="text-xs text-slate-500 flex items-center gap-3">
                    <span data-testid="tx-pagination-summary">
                      Showing <span className="font-semibold text-slate-700">{startIdx + 1}–{endIdx}</span> of <span className="font-semibold text-slate-700">{transactions.length}</span> transactions
                    </span>
                    <label className="flex items-center gap-1.5">
                      <span>Rows per page:</span>
                      <select
                        value={txPageSize}
                        onChange={(e) => { setTxPageSize(Number(e.target.value)); setTxPage(1); }}
                        className="border border-slate-200 rounded px-1.5 py-0.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-slate-300"
                        data-testid="tx-page-size-select"
                      >
                        {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </label>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setTxPage(1)}
                      disabled={currentPage === 1}
                      className="px-2.5 py-1 text-xs font-medium rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="tx-page-first"
                    >
                      « First
                    </button>
                    <button
                      onClick={() => setTxPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-2.5 py-1 text-xs font-medium rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="tx-page-prev"
                    >
                      ‹ Prev
                    </button>
                    <span className="px-3 text-xs text-slate-600" data-testid="tx-page-indicator">
                      Page <span className="font-semibold text-slate-800">{currentPage}</span> of <span className="font-semibold text-slate-800">{totalPages}</span>
                    </span>
                    <button
                      onClick={() => setTxPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-2.5 py-1 text-xs font-medium rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="tx-page-next"
                    >
                      Next ›
                    </button>
                    <button
                      onClick={() => setTxPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-2.5 py-1 text-xs font-medium rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="tx-page-last"
                    >
                      Last »
                    </button>
                  </div>
                </div>
                </>
                );
              })()}
            </div>
          )}

          {activeTab === 'messaging' && (
            <div data-testid="tab-messaging-content">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-[#0A2540]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>SMS History</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Messages sent to this subscriber via the SMS gateway</p>
                </div>
                <button
                  onClick={openSmsModal}
                  data-testid="btn-compose-sms"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0A2540] hover:bg-[#1E3A5F] text-white text-xs font-semibold uppercase tracking-wider transition-colors"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  <PaperPlaneTilt size={14} weight="bold" />
                  Compose
                </button>
              </div>
              {msgLoading ? <div className="py-2"><div className="space-y-2">{Array.from({length:4}).map((_,i)=>(<div key={i} className="skeleton h-14 rounded" style={{width:`${88 - (i%3)*5}%`}}/>))}</div></div>
              : messages.length === 0 ? <p className="text-slate-400 py-12 text-center text-sm" data-testid="messages-empty">No messages sent yet</p>
              : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="messages-table">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left py-3 px-3 text-[10px] tracking-[0.12em] uppercase text-slate-500 font-semibold">Date</th>
                        <th className="text-left py-3 px-3 text-[10px] tracking-[0.12em] uppercase text-slate-500 font-semibold">Mobile</th>
                        <th className="text-left py-3 px-3 text-[10px] tracking-[0.12em] uppercase text-slate-500 font-semibold">Message</th>
                        <th className="text-left py-3 px-3 text-[10px] tracking-[0.12em] uppercase text-slate-500 font-semibold">Status</th>
                        <th className="text-left py-3 px-3 text-[10px] tracking-[0.12em] uppercase text-slate-500 font-semibold">Sender</th>
                      </tr>
                    </thead>
                    <tbody>
                      {messages.slice(0, 5).map((m, i) => {
                        const ok = (m.status || 'sent') === 'sent';
                        return (
                          <tr key={i} className="border-b border-slate-100">
                            <td className="py-3 px-3 text-slate-500 whitespace-nowrap font-mono text-xs">{(m.created_at || '').substring(0, 16).replace('T', ' ')}</td>
                            <td className="py-3 px-3 font-mono">{m.phone || sub.mobile}</td>
                            <td className="py-3 px-3 text-slate-700 max-w-md">{m.message || '-'}</td>
                            <td className="py-3 px-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full ${
                                ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                              }`}>
                                {ok ? <CheckCircle size={10} weight="bold" /> : <WarningCircle size={10} weight="bold" />}
                                {m.status || 'sent'}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-slate-500">{m.sender_username || 'SYSTEM'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'renewal' && (
            <div className="max-w-3xl space-y-6" data-testid="tab-renewal-content">
              <div className="bg-[#FFFFFF] border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-3 bg-gradient-to-r from-[#0A2540]/5 via-white to-[#0A2540]/5 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-[#0A2540] flex items-center justify-center">
                      <CreditCard size={14} weight="bold" className="text-white" />
                    </div>
                    <p className="text-[11px] uppercase tracking-widest text-[#0A2540] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      Card Renewal
                    </p>
                  </div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    Fields marked * are required
                  </p>
                </div>

                <div className="p-5 space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FField label="Old Card"><Input value={sub.card_no || ''} readOnly className="bg-slate-50" /></FField>
                    <FField label="New Card" required>
                      <Input value={renewalForm.new_card} onChange={e => setRenewalForm(p => ({ ...p, new_card: e.target.value.replace(/\D/g, '') }))} placeholder="1370004171252" inputMode="numeric" data-testid="renewal-new-card" />
                    </FField>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FField label="Plan" required>
                      <Select value={renewalForm.plan} onValueChange={v => setRenewalForm(p => ({ ...p, plan: v }))}>
                        <SelectTrigger data-testid="renewal-plan"><SelectValue placeholder="Select plan" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Fee">Paid</SelectItem>
                          <SelectItem value="Free">Free</SelectItem>
                        </SelectContent>
                      </Select>
                    </FField>
                    <FField label="Reason" required>
                      <Select value={renewalForm.reason} onValueChange={v => setRenewalForm(p => ({ ...p, reason: v }))}>
                        <SelectTrigger data-testid="renewal-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Lost">Lost</SelectItem>
                          <SelectItem value="Damaged">Damaged</SelectItem>
                          <SelectItem value="malfunction">Malfunction</SelectItem>
                        </SelectContent>
                      </Select>
                    </FField>
                  </div>

                  <FField label="Cashier" required>
                    {(() => {
                      // Only cashiers whose linked user has a mobile qualify (middleware agent_id = phone)
                      const eligible = cashiers
                        .filter(c => !sub.entity_id || String(c.entity_id) === String(sub.entity_id))
                        .filter(c => c.user_mobile && String(c.user_mobile).replace(/\D/g, '').length >= 9);
                      const selected = eligible.find(c => String(c.user_mobile).replace(/\D/g, '') === renewalForm.agent_id);
                      return (
                        <>
                          <SearchableCashierSelect
                            value={renewalForm.agent_id}
                            onChange={(v) => setRenewalForm(p => ({ ...p, agent_id: v }))}
                            cashiers={eligible}
                            disabled={cashiersLoading || eligible.length === 0}
                            placeholder={
                              cashiersLoading
                                ? 'Loading cashiers…'
                                : eligible.length === 0
                                  ? 'No cashiers with a mobile on file'
                                  : 'Select cashier'
                            }
                            visibleCount={6}
                            testid="renewal-cashier"
                          />
                          {selected && (
                            <div className="grid grid-cols-2 gap-2 mt-2">
                              <div className="border border-slate-200 rounded-md px-3 py-2 bg-slate-50">
                                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Float Balance</p>
                                <p className="text-sm font-bold text-[#0A2540]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                  {Number(selected.assigned_float || 0).toLocaleString()}
                                </p>
                              </div>
                              <div className="border border-slate-200 rounded-md px-3 py-2 bg-slate-50">
                                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Card Balance</p>
                                <p className="text-sm font-bold text-[#10B981]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                  {Number(selected.assigned_cards || 0).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </FField>

                  <FField label="Description">
                    <textarea value={renewalForm.description} onChange={e => setRenewalForm(p => ({ ...p, description: e.target.value }))} className="w-full border border-slate-200 rounded-md p-3 text-sm min-h-[90px] resize-y focus:outline-none focus:ring-1 focus:ring-slate-400" placeholder="Short note (optional)" data-testid="renewal-description" />
                  </FField>
                </div>

                <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex gap-3 justify-end">
                  <button onClick={() => setRenewalForm({ new_card: '', plan: 'Fee', reason: '', description: '', agent_id: '' })} disabled={renewalSubmitting}
                    className="px-5 py-2.5 text-xs font-semibold uppercase tracking-wider border border-slate-300 rounded-md hover:bg-slate-100 transition-colors disabled:opacity-50" data-testid="renewal-reset">
                    Reset
                  </button>
                  <button onClick={async () => {
                    if (!renewalForm.new_card || !renewalForm.reason) { toast.error('New card and reason are required'); return; }
                    if (!renewalForm.agent_id) { toast.error('Please select the cashier handling the renewal'); return; }
                    if (!sub.middleware_id) { toast.error('Subscriber is not synced with the middleware'); return; }
                    setRenewalSubmitting(true);
                    try {
                      const { data } = await axios.post(
                        `${API_URL}/api/subscribers/${id}/renew-card`,
                        { new_card: renewalForm.new_card, plan: renewalForm.plan, reason: renewalForm.reason, description: renewalForm.description, agent_id: renewalForm.agent_id || '' },
                        { withCredentials: true, headers: hdrs() }
                      );
                      toast.success(`Card renewed · ${data.old_card || '—'} → ${data.new_card}${data.fee ? ` · Fee ${Number(data.fee).toLocaleString()}` : ''}`);
                      setRenewalForm({ new_card: '', plan: 'Fee', reason: '', description: '', agent_id: '' });
                      fetchSubscriber();
                    } catch (e) {
                      toast.error(formatApiError(e, 'Card renewal failed'));
                    } finally { setRenewalSubmitting(false); }
                  }} disabled={renewalSubmitting}
                    className="px-5 py-2.5 text-xs font-semibold uppercase tracking-wider bg-slate-900 text-white rounded-md hover:bg-slate-700 transition-colors disabled:opacity-60" data-testid="renewal-submit">
                    {renewalSubmitting ? 'Renewing…' : 'Renew Card'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ===== SMS Modal ===== */}
      <Dialog open={smsOpen} onOpenChange={setSmsOpen}>
        <DialogContent className="sm:max-w-[520px]" data-testid="sms-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0A2540]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              <PaperPlaneTilt size={20} weight="duotone" className="text-[#0A2540]" />
              SMS Subscriber
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              The message will be delivered via the configured SMS gateway and recorded under Messages.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.12em] text-slate-500 font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Full Name</Label>
              <Input value={(sub.sub_name || '').toUpperCase()} readOnly className="bg-slate-50 font-medium" data-testid="sms-full-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.12em] text-slate-500 font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Card No.</Label>
                <Input value={sub.card_no || '—'} readOnly className="bg-slate-50 font-mono" data-testid="sms-card-no" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.12em] text-slate-500 font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Mobile</Label>
                <Input value={sub.mobile || '—'} readOnly className="bg-slate-50 font-mono" data-testid="sms-mobile" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.12em] text-slate-500 font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Message <span className="text-rose-500">*</span>
              </Label>
              <Textarea
                value={smsBody}
                onChange={(e) => setSmsBody(e.target.value)}
                placeholder="Type your message…"
                rows={5}
                maxLength={1000}
                required
                data-testid="sms-message"
                className="resize-none"
              />
              <div className="flex justify-between text-[10px] text-slate-400 font-mono">
                <span>{Math.ceil((smsBody.length || 0) / 160) || 0} SMS part(s) · 160 chars each</span>
                <span>{smsBody.length}/1000</span>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <button
              type="button"
              onClick={() => setSmsOpen(false)}
              disabled={smsSending}
              data-testid="sms-close"
              className="px-4 py-2 text-xs font-semibold uppercase tracking-wider bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md transition-colors disabled:opacity-60"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              Close
            </button>
            <button
              type="button"
              onClick={submitSms}
              disabled={smsSending || !smsBody.trim() || !sub.mobile}
              data-testid="sms-send"
              className="inline-flex items-center gap-2 px-5 py-2 text-xs font-semibold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors disabled:opacity-60"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {smsSending ? <Spinner size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />}
              {smsSending ? 'Sending…' : 'Send'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ================= SUBSCRIBER HERO (replaces IC card) ================= */
function SubscriberHero({
  sub, name, bal, currency, totalRecharges, rechargeCount, avgRecharge, lastRecharge, daysSinceReg,
  monthlyBuckets, momChange, lastMonthAmt,
  isEditing, saving, onSave, onEdit, onCancel,
  stmtFrom, stmtTo, onStmtFromChange, onStmtToChange, onGenerateStmt,
  isSuperAdmin, onSendSms,
}) {
  const cardNo = sub.card_no || '—';
  // Status code mapping (per product spec, Feb 2026): N→Normal, G→Good,
  // E→Excellent, D→Dormant. Waba-pending (registered without a card yet)
  // takes precedence and surfaces as its own "Pending" tier.
  const isPending = (sub.is_waba_reg === 'Y' && !sub.card_no);
  const statusCode = String(sub.status || sub.sub_status || '').trim().toUpperCase().slice(0, 1);

  const perf = (() => {
    if (isPending)          return { label: 'Pending',   dot: 'bg-amber-500',   text: 'text-amber-700',   bg: 'bg-amber-50' };
    if (statusCode === 'E') return { label: 'Excellent', dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50' };
    if (statusCode === 'G') return { label: 'Good',      dot: 'bg-[#0A2540]',   text: 'text-[#0A2540]',   bg: 'bg-[#0A2540]/10' };
    if (statusCode === 'D') return { label: 'Dormant',   dot: 'bg-rose-500',    text: 'text-rose-700',    bg: 'bg-rose-50' };
    // "N" + legacy "active" + blank → Normal
    return { label: 'Normal', dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50' };
  })();

  // Sparkline path for the last 6 months recharges
  const sparkPath = (() => {
    const vals = (monthlyBuckets || []).map(b => Number(b.r || 0));
    if (!vals.length) return '';
    const max = Math.max(...vals, 1);
    const w = 100, h = 32;
    return vals.map((v, i) => {
      const x = (i / (vals.length - 1 || 1)) * w;
      const y = h - (v / max) * h;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    }).join(' ');
  })();

  return (
    <div className="rounded-2xl overflow-hidden border border-[#EDE6D8] bg-[#FFFFFF] shadow-[0_1px_3px_rgba(15,23,42,0.04)]" data-testid="subscriber-hero">
      {/* Top brand band */}
      <div
        className="relative px-6 py-5"
        style={{ background: 'linear-gradient(135deg, #0A2540 0%, #1E3A5F 100%)' }}
      >
        {/* abstract pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 400 80" preserveAspectRatio="none" aria-hidden>
          <circle cx="380" cy="10" r="60" fill="white" />
          <circle cx="20"  cy="70" r="80" fill="white" />
        </svg>

        <div className="relative flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <div className="shrink-0 w-12 h-12 rounded-xl bg-[#FFFFFF]/15 backdrop-blur flex items-center justify-center">
              <CreditCard size={22} className="text-white" weight="duotone" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-white/70 font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Subscriber Profile
              </p>
              <p className="text-base font-medium text-white truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Card · <span className="font-mono">{cardNo}</span>
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <HeroAction onClick={onSave}   disabled={saving} icon={FloppyDisk} label={saving ? 'Saving…' : 'Save'} testid="btn-save" primary />
                <HeroAction onClick={onCancel} disabled={saving} icon={X}          label="Cancel"  testid="btn-cancel" />
              </>
            ) : (
              <HeroAction onClick={onEdit}                       icon={PencilSimple} label="Edit"   testid="btn-edit" />
            )}
            <HeroAction onClick={onSendSms} icon={ChatText} label="SMS" testid="btn-sms" />
          </div>
        </div>
      </div>

      {/* Identity strip */}
      <div className="px-6 py-5 border-b border-[#EDE6D8]">
        <div className="flex flex-wrap items-center gap-5">
          {/* Name + meta */}
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-[#1C1917] truncate capitalize" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="hero-name">
              {(name || '—').toLowerCase()}
            </h2>
            <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-1 text-sm text-[#78716C]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              <span className="inline-flex items-center gap-1.5 truncate">
                <GraduationCap size={14} className="shrink-0 text-[#A8A29E]" weight="duotone" />
                {sub.campus_name || '—'}
              </span>
              <span className="text-[#EDE6D8]">·</span>
              <span className="inline-flex items-center gap-1.5">
                <IdentificationBadge size={14} className="shrink-0 text-[#A8A29E]" weight="duotone" />
                <span className="font-mono text-xs">{sub.category || '—'}</span>
              </span>
              {sub.mobile && (
                <>
                  <span className="text-[#EDE6D8]">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Phone size={14} className="shrink-0 text-[#A8A29E]" weight="duotone" />
                    <span className="font-mono text-xs">{sub.mobile}</span>
                  </span>
                </>
              )}
              {isSuperAdmin && sub.entity_name && (
                <>
                  <span className="text-[#EDE6D8]">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Buildings size={14} className="shrink-0 text-[#A8A29E]" weight="duotone" />
                    <span className="text-xs">{sub.entity_name}</span>
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Performance pill */}
          <div className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${perf.bg}`} data-testid="hero-perf">
            <span className={`w-1.5 h-1.5 rounded-full ${perf.dot}`} />
            <span className={`text-xs font-semibold ${perf.text}`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {perf.label}
            </span>
          </div>
        </div>
      </div>

      {/* Stats + sparkline + statement */}
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hero-stats">
          <HeroBalanceStat label="Wallet Balance" value={bal} sub={currency || ''} accent="#0A2540" testid="stat-balance" />
          <HeroStat icon={Lightning}  label="Total Recharges" value={totalRecharges.toLocaleString()} sub={`${rechargeCount} top-up${rechargeCount === 1 ? '' : 's'}`}        accent="#0A2540" testid="stat-recharges" />
          <HeroStat
            icon={TrendUp}
            label="Avg Recharge"
            value={Math.round(avgRecharge).toLocaleString()}
            sub={lastMonthAmt > 0 ? `${momChange >= 0 ? '+' : ''}${momChange.toFixed(0)}% MoM` : 'First active month'}
            accent="#1E3A5F"
            testid="stat-avg"
            momPositive={momChange >= 0}
            footer={(() => {
              if (!lastRecharge) return null;
              const ts = lastRecharge.created_at;
              if (!ts) return null;
              // Upstream may emit either "YYYY-MM-DD HH:MM:SS" (space) or ISO
              // (with "T"). Replace the space so Safari/Firefox can parse it.
              const dt = new Date(String(ts).replace(' ', 'T'));
              if (Number.isNaN(dt.getTime())) return null;
              const diffMs = Date.now() - dt.getTime();
              const diffMin = Math.max(0, Math.floor(diffMs / 60000));
              let when;
              if (diffMin < 1)       when = 'just now';
              else if (diffMin < 60) when = `${diffMin}m ago`;
              else if (diffMin < 60 * 24) {
                const h = Math.floor(diffMin / 60);
                when = `${h}h ago`;
              } else {
                const d = Math.floor(diffMin / (60 * 24));
                when = `${d} day${d === 1 ? '' : 's'} ago`;
              }
              const amt = Number(lastRecharge.amount || 0).toLocaleString();
              return `Last top-up · ${when} · ${amt} ${currency || ''}`.trim();
            })()}
          />
          <HeroStat icon={CreditCard} label="Member Since"    value={`${daysSinceReg}d`}              sub={sub.created_at ? sub.created_at.substring(0,10) : '—'} accent="#1C1917" testid="stat-since" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2 rounded-xl border border-[#EDE6D8] bg-gradient-to-br from-slate-50/60 to-white p-4 flex items-center gap-4" data-testid="hero-sparkline">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#A8A29E] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>6-Month Recharge Trend</p>
              <p className="mt-1 text-sm text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                <span className="font-bold">{(monthlyBuckets[monthlyBuckets.length - 1]?.r || 0).toLocaleString()}</span>
                <span className="text-[#A8A29E]"> {currency || ''} this month</span>
              </p>
            </div>
            <div className="shrink-0">
              <svg width="160" height="40" viewBox="0 0 100 32" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="sparkfill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor="#0A2540" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#0A2540" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {sparkPath && (
                  <>
                    <path d={`${sparkPath} L 100 32 L 0 32 Z`} fill="url(#sparkfill)" />
                    <path d={sparkPath} fill="none" stroke="#0A2540" strokeWidth="1.5" />
                  </>
                )}
              </svg>
            </div>
          </div>
          <div className="rounded-xl border border-[#EDE6D8] bg-gradient-to-br from-slate-50/60 to-white p-4" data-testid="hero-stmt">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#A8A29E] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Generate Statement</p>
            <div className="mt-2 flex items-center gap-2">
              <input type="date" value={stmtFrom} onChange={(e) => onStmtFromChange(e.target.value)} className="h-9 text-xs border border-[#EDE6D8] rounded-lg px-2 flex-1 min-w-0 focus:outline-none focus:border-[#0A2540]" data-testid="stmt-from" />
              <input type="date" value={stmtTo}   onChange={(e) => onStmtToChange(e.target.value)}   className="h-9 text-xs border border-[#EDE6D8] rounded-lg px-2 flex-1 min-w-0 focus:outline-none focus:border-[#0A2540]" data-testid="stmt-to" />
              <button onClick={onGenerateStmt} title="Download statement" aria-label="Download statement" className="h-9 w-9 shrink-0 bg-[#0A2540] text-white rounded-lg hover:bg-[#0A2540]/90 transition-colors flex items-center justify-center" data-testid="btn-download-stmt">
                <Download size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroAction({ icon: Icon, label, onClick, disabled, testid, primary, danger }) {
  const base = 'inline-flex items-center justify-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold transition-all disabled:opacity-50';
  const cls = primary
    ? 'bg-[#FFFFFF] text-[#0A2540] hover:bg-[#FFFFFF]/90'
    : danger
      ? 'bg-[#FFFFFF]/10 text-white hover:bg-rose-500 hover:text-white border border-white/25'
      : 'bg-[#FFFFFF]/10 text-white hover:bg-[#FFFFFF]/20 border border-white/25';
  return (
    <button onClick={onClick} disabled={disabled} title={label} data-testid={testid} className={`${base} ${cls}`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
      <Icon size={14} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function HeroStat({ icon: Icon, label, value, sub, accent, testid, momPositive, footer }) {
  return (
    <div className="rounded-xl border border-[#EDE6D8] bg-[#FFFFFF] p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#A8A29E] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}15` }}>
          <Icon size={14} style={{ color: accent }} weight="duotone" />
        </div>
      </div>
      <p className="mt-2 text-2xl font-bold leading-none" style={{ fontFamily: 'JetBrains Mono, monospace', color: accent }}>{value}</p>
      {sub && <p className={`text-[11px] mt-1.5 ${momPositive === false ? 'text-rose-600' : 'text-[#A8A29E]'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>{sub}</p>}
      {footer && (
        <p className="text-[10px] mt-1.5 pt-1.5 border-t border-[#EDE6D8] text-[#78716C] truncate" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid={`${testid}-footer`}>
          {footer}
        </p>
      )}
    </div>
  );
}

function HeroBalanceStat({ label, value, sub, accent, testid }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="rounded-xl border border-[#EDE6D8] bg-[#FFFFFF] p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#A8A29E] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
        <button
          type="button"
          onClick={() => setVisible(v => !v)}
          className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:bg-[#0A2540]/10"
          style={{ backgroundColor: `${accent}15` }}
          aria-label={visible ? 'Hide balance' : 'Show balance'}
          data-testid={`${testid}-toggle`}
        >
          {visible
            ? <EyeSlash size={14} style={{ color: accent }} weight="duotone" />
            : <Eye size={14} style={{ color: accent }} weight="duotone" />}
        </button>
      </div>
      <p className="mt-2 text-2xl font-bold leading-none tracking-wide" style={{ fontFamily: 'JetBrains Mono, monospace', color: accent }}>
        {visible ? Number(value || 0).toLocaleString() : '••••••'}
      </p>
      {sub && <p className="text-[11px] mt-1.5 text-[#A8A29E]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{sub}</p>}
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub: subText, color, testId }) {
  return (
    <div className="bg-[#FFFFFF] border border-slate-200 rounded-xl p-4 flex flex-col justify-between min-h-[110px] hover:shadow-sm hover:-translate-y-0.5 transition-all" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-[0.15em] uppercase text-slate-400 font-semibold">{label}</p>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon size={14} style={{ color }} weight="duotone" />
        </div>
      </div>
      <div>
        <p className="text-2xl font-bold tracking-tight leading-none" style={{ color }}>{value}</p>
        {subText && <p className="text-[11px] text-slate-400 mt-1.5">{subText}</p>}
      </div>
    </div>
  );
}

function FField({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] tracking-wide uppercase text-slate-500 font-semibold">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
